import axios from "axios";
import Vue from "vue";
import { Toast } from "vant";

const _this = Vue.prototype;

// 同一次会话内最多允许跳转微信授权的次数。
// 后端持续异常时，旧代码会在「授权 -> 换取失败 -> 再授权」之间无限打转，
// 这里加个上限，撞到就停下来给用户一句明确的话。
const MAX_AUTH_REDIRECT = 2;
const REDIRECT_COUNT_KEY = "oauthRedirectCount";
// 记录已经拿去换过 openid 的 code。微信的 code 是一次性的，
// 重复使用必定返回 40163，所以同一个 code 绝不发第二次请求。
const USED_CODE_KEY = "oauthUsedCode";

// sessionStorage 在部分隐私模式 / 老 WebView 下会抛异常，统一包一层
function ssGet(key) {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function ssSet(key, value) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // 存不下就算了，只是少一层保护，不影响主流程
  }
}

function lsGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function lsSet(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // 同上
  }
}

// 最近一次换取失败的描述，撞到跳转上限时一并显示出来，
// 免得用户只看到一句「反复失败」却不知道到底为什么
let lastFailureText = "";

/**
 * 生成一个不含 code / state 的当前页地址。
 *
 * 两处都要用它：
 * 1. 换取成功 / 失败后清理地址栏，避免页面重载时拿旧 code 重放；
 * 2. 作为微信授权的 redirect_uri —— 如果把带 code 的地址传过去，
 *    微信会再追加一个 code，回来后 URL 上有两个 code 参数，
 *    而 getUrlKey 取到的是靠前的那个旧 code，于是永远换不出 openid。
 *
 * 只摘掉 code 和 state，其余业务参数原样保留。
 */
function buildCleanUrl() {
  const href = window.location.href;
  const qIndex = href.indexOf("?");
  if (qIndex === -1) return href;

  const hashIndex = href.indexOf("#");
  const base = href.substring(0, qIndex);
  const hash = hashIndex === -1 ? "" : href.substring(hashIndex);
  const rawQuery = href.substring(
    qIndex + 1,
    hashIndex === -1 ? href.length : hashIndex
  );

  const kept = rawQuery.split("&").filter(function (kv) {
    return kv && !/^(code|state)=/.test(kv);
  });

  return base + (kept.length ? "?" + kept.join("&") : "") + hash;
}

// 把地址栏上的 code / state 抹掉，不产生历史记录、不触发页面刷新
function stripCodeFromUrl() {
  const cleaned = buildCleanUrl();
  if (cleaned === window.location.href) return;
  try {
    window.history.replaceState(null, "", cleaned);
  } catch (e) {
    // 个别老旧 WebView 不支持 replaceState，忽略即可
  }
}

// 此针对openid为字符串null的情况,删除本地openid重新获取最新的code
function initCodeApi() {
  localStorage.removeItem("openId");
  getCodeApi(buildCleanUrl());
}

// 获取url上指定的参数
function getUrlKey(name) {
  return (
    decodeURIComponent(
      (new RegExp("[?|&]" + name + "=" + "([^&;]+?)(&|#|;|$)").exec(
        location.href
      ) || [, ""])[1].replace(/\+/g, "%20")
    ) || null
  );
}

// 是否运行在微信内置浏览器里（手机微信、PC 微信、微信开发者工具的 UA 都带 MicroMessenger）
function isWeixinBrowser() {
  return /micromessenger/i.test(navigator.userAgent || "");
}

// 跳转微信网页授权，换回一个新的 code
function getCodeApi(origlUrl) {
  // 网页授权只在微信内置浏览器里有效。在 Safari / Chrome 等手机浏览器里跳过去，
  // 用户只会看到腾讯那个「请在微信客户端打开链接」的死页面，而且回不来。
  // 与其把人甩出去，不如留在原地说清楚该怎么打开。
  if (!isWeixinBrowser()) {
    Toast({
      message:
        "请在微信中打开本页面（公众号菜单进入），浏览器中无法完成微信登录",
      duration: 0,
    });
    console.warn(
      "[getOpenid] 非微信环境，已阻止跳转授权。UA:",
      navigator.userAgent
    );
    return;
  }

  const redirected = parseInt(ssGet(REDIRECT_COUNT_KEY) || "0", 10) || 0;
  if (redirected >= MAX_AUTH_REDIRECT) {
    Toast({
      message: lastFailureText
        ? "微信登录反复失败：" + lastFailureText + "，请联系管理员"
        : "微信授权反复失败，请彻底退出公众号后重新进入",
      duration: 8000,
    });
    console.error(
      "[getOpenid] 授权跳转已达上限 " + MAX_AUTH_REDIRECT + " 次，停止跳转"
    );
    return;
  }
  ssSet(REDIRECT_COUNT_KEY, String(redirected + 1));

  const redUrl = origlUrl ? origlUrl : buildCleanUrl();
  const urlNow = encodeURIComponent(redUrl);
  const scope = "snsapi_base"; // 静默授权 用户无感知
  const url = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${_this.GLOBAL.appid}&redirect_uri=${urlNow}&response_type=code&scope=${scope}&state=123#wechat_redirect`;
  window.location.href = url;
}

// 把后端 / 微信返回的错误拼成一句人能看懂、又能照着排查的提示
function describeFailure(payload) {
  const parts = [];
  const message = payload && (payload.message || payload.msg);
  if (message) parts.push(message);

  // 后端如果透传了微信的 errcode，一并显示出来，省得每次都要抓包
  const errcode =
    payload &&
    (payload.errcode ||
      (payload.data && payload.data.errcode) ||
      (payload.error && payload.error.errcode));
  if (errcode) parts.push("微信错误码 " + errcode);

  return parts.length ? parts.join("，") : "未知错误";
}

// 从错误文本里抠出微信的 errcode。后端是把微信原文拼进 message 的，
// 形如 "获取openid失败: errcode=40029, errmsg=invalid code, rid: ..."
function extractWxErrcode(payload) {
  if (!payload) return null;
  if (payload.errcode) return String(payload.errcode);
  const text = payload.message || payload.msg || "";
  const m = /errcode[=:]\s*(\d+)/i.exec(text);
  return m ? m[1] : null;
}

// 这几个错误码都是「这个 code 不能用了」，换一个新的就有可能成功：
// 40029 invalid code（已用过 / 已过期 / 不是本号发的）
// 40163 code been used
// 42003 oauth_code timeout
const RETRIABLE_WX_ERRCODES = ["40029", "40163", "42003"];

// 获取openId
function getOpenidApi(code, callback) {
  // 这个 code 之前已经换过一次了，再发一次必定失败。
  // 直接清掉地址栏重新走授权，拿一个新的。
  // 用 localStorage 而不是 sessionStorage：iOS 微信有时会先在一个隐藏
  // WebView 里预加载页面、再开真正的那个，两者 sessionStorage 不互通，
  // 同一个 code 会被消费两次，第二次必然 40029。
  if (lsGet(USED_CODE_KEY) === code) {
    console.warn("[getOpenid] code 已被使用过，重新发起授权:", code);
    stripCodeFromUrl();
    getCodeApi();
    return;
  }
  lsSet(USED_CODE_KEY, code);

  axios
    .get(_this.GLOBAL.ssoServer + "/gzh/getOpenidByCode", {
      params: {
        code: code,
      },
    })
    .then(function (response) {
      const payload = response.data;
      const bizCode = payload && payload.code;

      if (bizCode !== "0000000") {
        console.error("[getOpenid] 换取 openid 失败", {
          请求的code: code,
          业务码: bizCode,
          完整响应: payload,
        });
        localStorage.removeItem("openId");
        // 失败也要清地址栏，否则用户一刷新又拿这个死 code 撞一次
        stripCodeFromUrl();

        // 如果只是这个 code 废了（被预加载抢先消费、超时等），自动换一个新的重来。
        // getCodeApi 内部有 MAX_AUTH_REDIRECT 上限，不会无限打转；
        // 撞到上限还失败，说明是配置层面的问题，那时才把错误摊给用户看。
        lastFailureText = describeFailure(payload);
        const errcode = extractWxErrcode(payload);
        if (errcode && RETRIABLE_WX_ERRCODES.indexOf(errcode) !== -1) {
          console.warn(
            "[getOpenid] errcode=" + errcode + "，自动重新授权换新 code"
          );
          getCodeApi();
          return;
        }

        Toast({
          message:
            "微信登录失败：" + describeFailure(payload) + "，请退出重新进入",
          duration: 5000,
        });
        return;
      }

      const openid = payload && payload.data;
      // 后端偶尔会回「成功但 data 为空」，旧代码会把字符串 "null" 存进
      // localStorage，然后各个页面各自去判断 === "null"。在源头挡住。
      if (!openid || openid === "null") {
        console.error("[getOpenid] 后端返回成功但没有 openid", payload);
        Toast({
          message: "微信登录未完成（未取到用户标识），请退出重新进入",
          duration: 5000,
        });
        localStorage.removeItem("openId");
        stripCodeFromUrl();
        return;
      }

      localStorage.setItem("openId", openid);
      // 成功了就把两个计数 / 标记归零，下次进来是干净的
      ssSet(REDIRECT_COUNT_KEY, "0");
      stripCodeFromUrl();
      callback ? callback() : "";
    })
    .catch((err) => {
      // 旧代码这里只 console.log，网络异常时用户什么都看不到，
      // 只会看见一个空页面，还以为是自己网不好或者页面坏了
      console.error("[getOpenid] 请求异常", err);
      Toast({
        message: "网络异常，无法完成微信登录，请检查网络后重新进入",
        duration: 5000,
      });
      localStorage.removeItem("openId");
      stripCodeFromUrl();
    });
}

// 初始化
function initOpenid(callback) {
  const code = getUrlKey("code");
  if (code) {
    //拿到code， code传递给后台接口换取opend
    getOpenidApi(code, callback);
  } else {
    getCodeApi();
  }
}

export { initOpenid, getUrlKey, initCodeApi };
